import React, { useEffect, useContext, useCallback } from 'react';
import { PAGED_APPROVAL_TYPES_QUERY } from './queries';
import { APPROVAL_TYPE_DELETED } from './subscriptions';
import { ServiceContext } from 'providers/ServiceProvider';
import ExportAction from './actions/ExportAction';
import { useTranslation } from 'react-i18next';
import { Card } from '@totalsoft/rocket-ui';
import { useApolloClient } from '@apollo/client';
import { getListBrowserStore, useListBrowserUtils } from '@ts-private/list-browser';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { useToast } from 'hooks/toasts/useToast';
import { useMemo } from 'react';
import TableListBrowser from 'features/common/components/table-list-browser/TableListBrowser';
import ApprovalTypeItem from './ApprovalTypeItem';
import ApolloFetchingComponent from 'features/common/components/apollo-fetching/ApolloFetchingComponent';
import { DELETE_APPROVAL_TYPE } from './mutations';
import { useMutation } from 'hooks/apollo/useMutation';
import useConfirmDialog from 'features/common/components/confirm/useConfirmDialog';
import useSubscription from 'hooks/apollo/useSubscription';

export const approvalTypesStore = getListBrowserStore({
  filterDefinitions: [
    {
      name: 'approvalTypeCode',
      caption: 'ApprovalTypes.ApprovalTypeCode',
      type: 'text'
    },
    {
      name: 'approvalTypeName',
      caption: 'ApprovalTypes.ApprovalTypeName',
      type: 'text'
    }
  ],
  orderByFields: [
    {
      caption: 'ApprovalTypes.ApprovalTypeName',
      value: 'ApprovalTypeName'
    },
    {
      caption: 'ApprovalTypes.ApprovalTypeCode',
      value: 'ApprovalTypeCode'
    }
  ],
  pager: {
    orderBy: 'ApprovalTypeName',
    direction: 'asc'
  }
});

const ApprovalTypeListBrowser = () => {
  const [t] = useTranslation();
  const addToast = useToast();
  const client = useApolloClient();
  const confirm = useConfirmDialog();
  const [activeService] = useContext(ServiceContext);
  const { addActions } = useListBrowserUtils(approvalTypesStore);

  const fetchingOptions = useMemo(
    () => ({
      queryOptions: {
        query: PAGED_APPROVAL_TYPES_QUERY,
        name: 'pagedApprovalTypes'
      }
    }),
    []
  );

  // #region Delete

  useSubscription(APPROVAL_TYPE_DELETED, {
    onData: ({ data: { data }, client }) => {
      addToast(t('ApprovalTypes.ApprovalTypeDeleted'), 'success');
      client.cache.evict({ id: `ApprovalTypeItem:{"approvalTypeId":${data.approvalTypeDeleted.approvalTypeId}}` });
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'pagedApprovalTypes' });
      client.cache.gc();
    }
  });

  const [deleteApprovalType] = useMutation(DELETE_APPROVAL_TYPE);

  const handleDelete = useCallback(
    (approvalTypeId, siteId) => {
      confirm(
        () => {
          deleteApprovalType({ variables: { input: { approvalTypeId, siteId } } });
        },
        {
          content: t('ApprovalTypes.DeleteApprovalType')
        }
      );
    },
    [deleteApprovalType, confirm, t]
  );

  // #endregion

  useEffect(() => {
    if (activeService) {
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'pagedApprovalTypes' });
      client.cache.gc();
    }
  }, [activeService, client]);

  // TODO Wrong way to add actions to list-browser!! Will need a refactor
  useEffect(() => {
    addActions([
      {
        name: 'export',
        icon: <ExportAction />
      }
    ]);
  }, [addActions]);

  const itemComponent = useCallback(({ data }) => <ApprovalTypeItem data={data} onDelete={handleDelete} />, [handleDelete]);

  const columns = useMemo(
    () => [
      { name: 'approvalTypeName', caption: 'ApprovalTypes.ApprovalTypeName' },
      { name: 'approvalTypeCode', caption: 'ApprovalTypes.ApprovalTypeCode' },
      { name: 'actions', caption: 'Actions' }
    ],
    []
  );

  return (
    <Card icon={ListAltIcon}>
      <TableListBrowser name='approval-types-browser' store={approvalTypesStore} ItemComponent={itemComponent} columns={columns} />
      <ApolloFetchingComponent store={approvalTypesStore} options={fetchingOptions} />
    </Card>
  );
};

export default ApprovalTypeListBrowser;
